import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const STATUS_CLS = { new: "bg-emerald-100 text-emerald-800", exists: "bg-amber-100 text-amber-800", error: "bg-red-100 text-red-800" };

export async function downloadTemplate(kind) {
  const { data } = await api.get(`/bulk/${kind}/template`, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(data);
  a.download = `template-${kind}.xlsx`;
  a.click();
}

function PreviewTable({ rows, t }) {
  return (
    <div className="max-h-[40vh] overflow-auto border border-slate-200 rounded-md" data-testid="bulk-preview-table">
      <table className="min-w-full text-sm">
        <thead className="bg-slate-50 sticky top-0 text-xs uppercase text-slate-500">
          <tr><th className="text-left px-3 py-2">{t("row")}</th><th className="text-left px-3 py-2">{t("data")}</th><th className="text-left px-3 py-2">{t("status")}</th><th className="text-left px-3 py-2">{t("note")}</th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-100" data-testid={`bulk-row-${r.row}`}>
              <td className="px-3 py-1.5 font-mono text-xs">{r.row}</td>
              <td className="px-3 py-1.5 font-mono text-xs">{r.key}</td>
              <td className="px-3 py-1.5"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_CLS[r.status]}`}>{t(`bulk_${r.status}`)}</span></td>
              <td className="px-3 py-1.5 text-xs text-slate-600">{r.message}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function BulkUploadDialog({ kind, title, open, onClose, onDone }) {
  const { t } = useLang();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("skip");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setFile(null); setPreview(null); setMode("skip"); } }, [open]);

  const send = (path, extra = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(extra).forEach(([k, v]) => fd.append(k, v));
    return api.post(`/bulk/${kind}/${path}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
  };
  const run = async (fn) => {
    setBusy(true);
    try { await fn(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const check = () => run(async () => setPreview((await send("preview")).data));
  const commit = () => run(async () => {
    const { data } = await send("commit", { mode });
    toast.success(`${t("bulk_done")}: ${data.created} ${t("bulk_created")}, ${data.updated} ${t("bulk_updated")}, ${data.skipped} ${t("bulk_skipped")}`);
    onClose();
    onDone?.();
  });

  const s = preview?.summary;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="bulk-upload-dialog">
        <DialogHeader><DialogTitle>{t("bulk_upload")} · {title}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => downloadTemplate(kind)} data-testid="bulk-dialog-template-btn"><Download size={14} className="mr-2" />{t("download_template")}</Button>
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <input type="file" accept=".xlsx" className="hidden" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="bulk-file-input" />
              <span className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-sm">{t("choose_file")}</span>
              <span className="text-sm text-slate-600 max-w-[220px] truncate" data-testid="bulk-file-name">{file ? file.name : t("no_file")}</span>
            </label>
            <Button size="sm" disabled={!file || busy} onClick={check} data-testid="bulk-preview-btn">{t("bulk_check")}</Button>
          </div>
          <p className="text-xs text-slate-500">{t("bulk_hint")}</p>
          {preview && (
            <>
              <div className="flex flex-wrap gap-2 text-sm" data-testid="bulk-summary">
                {["new", "exists", "error"].map((k) => <span key={k} className={`px-2.5 py-1 rounded-md font-medium ${STATUS_CLS[k]}`}>{t(`bulk_${k}`)}: {s[k]}</span>)}
              </div>
              {s.exists > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2" data-testid="bulk-duplicate-warning">
                  <div className="flex items-center gap-2 font-medium text-amber-800"><AlertTriangle size={14} />{s.exists} {t("bulk_dup_warning")}</div>
                  {["update", "skip"].map((m) => (
                    <label key={m} className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" name="bulk-mode" checked={mode === m} onChange={() => setMode(m)} className="accent-blue-600" data-testid={`bulk-mode-${m}`} />
                      {t(`bulk_mode_${m}`)}
                    </label>
                  ))}
                </div>
              )}
              {s.error > 0 && <div className="text-sm text-red-700" data-testid="bulk-error-note">{t("bulk_fix_errors")}</div>}
              <PreviewTable rows={preview.rows} t={t} />
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="bulk-cancel-btn">{t("cancel")}</Button>
          <Button onClick={commit} disabled={!preview || s.error > 0 || busy || s.new + s.exists === 0}
                  className="bg-slate-900 hover:bg-slate-800" data-testid="bulk-commit-btn">{t("bulk_import")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function BulkButtons({ kind, title, onDone, testid = kind }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button size="sm" variant="outline" onClick={() => downloadTemplate(kind)} data-testid={`${testid}-template-btn`}><Download size={14} className="mr-2" />{t("download_template")}</Button>
      <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`${testid}-upload-btn`}><Upload size={14} className="mr-2" />{t("bulk_upload")}</Button>
      <BulkUploadDialog kind={kind} title={title} open={open} onClose={() => setOpen(false)} onDone={onDone} />
    </>
  );
}
